import { useState, useEffect, useCallback, useRef } from 'react';

const usePullToRefresh = (onRefresh, options = {}) => {
  const {
    threshold = 80,
    maxPull = 120,
    useHardReload = true, // Default to hard page reload
  } = options;

  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startY = useRef(0);
  const currentY = useRef(0);
  const isAtTop = useRef(true);

  // Check if running as PWA
  const isPWA = useRef(
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );

  const handleTouchStart = useCallback((e) => {
    // Check if at top - use both scrollY and document scroll
    const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
    isAtTop.current = scrollTop <= 0;

    if (isAtTop.current) {
      startY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || isRefreshing) return;

    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    // Check scroll position again
    const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;

    // Only pull down when at top
    if (diff > 0 && scrollTop <= 0) {
      // Apply resistance
      const resistance = 0.5;
      const distance = Math.min(diff * resistance, maxPull);
      setPullDistance(distance);

      // Prevent default scroll when pulling down
      if (distance > 5) {
        e.preventDefault();
      }
    } else if (diff < 0) {
      // Scrolling up, reset
      setPullDistance(0);
      setIsPulling(false);
    }
  }, [isPulling, isRefreshing, maxPull]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;

    setIsPulling(false);

    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(60); // Hold at refresh position

      // Small delay to show the refreshing state
      await new Promise(resolve => setTimeout(resolve, 300));

      // Trigger hard page reload (like native browser behavior)
      if (useHardReload) {
        window.location.reload();
        return; // Page will reload, no need to continue
      }

      // Fallback to callback if useHardReload is false
      try {
        if (onRefresh) {
          await onRefresh();
        }
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [isPulling, pullDistance, threshold, isRefreshing, onRefresh, useHardReload]);

  useEffect(() => {
    // Add listeners to document for better PWA compatibility
    const target = document;

    // Use capture phase for better PWA handling
    target.addEventListener('touchstart', handleTouchStart, { passive: true, capture: false });
    target.addEventListener('touchmove', handleTouchMove, { passive: false, capture: false });
    target.addEventListener('touchend', handleTouchEnd, { passive: true, capture: false });

    return () => {
      target.removeEventListener('touchstart', handleTouchStart, { capture: false });
      target.removeEventListener('touchmove', handleTouchMove, { capture: false });
      target.removeEventListener('touchend', handleTouchEnd, { capture: false });
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const PullToRefreshIndicator = () => (
    <div
      className="pull-to-refresh-indicator"
      style={{
        height: pullDistance,
        opacity: Math.min(pullDistance / threshold, 1),
        transition: isPulling ? 'none' : 'all 0.3s ease',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <div className={`ptr-spinner ${isRefreshing ? 'spinning' : ''}`}>
        {isRefreshing ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{
              transform: `rotate(${Math.min(pullDistance / threshold * 180, 180)}deg)`,
              transition: isPulling ? 'none' : 'transform 0.3s ease'
            }}
          >
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        )}
      </div>
      <span className="ptr-text">
        {isRefreshing ? 'Refreshing...' : pullDistance >= threshold ? 'Release to refresh' : 'Pull to refresh'}
      </span>
    </div>
  );

  return {
    isRefreshing,
    pullDistance,
    isPWA: isPWA.current,
    PullToRefreshIndicator,
  };
};

export default usePullToRefresh;
